import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Typography } from '@material-ui/core';
import SwiperCore, { EffectCoverflow, Lazy } from 'swiper/core';
import 'swiper/components/effect-coverflow/effect-coverflow.min.css';
import 'swiper/components/lazy/lazy.min.css';

import css from './galleryDetailed.module.scss';
import Dialog from '../../../components/dialog/Dialog';
import { GalleryImage, getImage } from '../../../services/models';
import { Swiper, SwiperClass, SwiperSlide } from '../../../components/elements/Swiper';
import { ContainButton, LeftArrowIconButton, RightArrowIconButton } from '../../../components/buttons/Buttons';
import { useUpdateEffect } from '../../../hooks/react';
import { DownloadIcon } from '../../../components/Icons';
import { ClassName } from '../../../components/types';
import { galleryDetailedRoute } from '../../../routes';
import { downloadImage } from '../../../utils/download';
import { getGalleryImage } from '../../../services/modelUtils';

SwiperCore.use([Lazy, EffectCoverflow]);

const coverflowEffect: any = {
  scale: 0.50,
  stretch: '25%',
  rotate: 0,
  depth: 0,
  slideShadows: false,
};

export default function GalleryDetailed({ images, selectedIndex = 0, updateHistory = false, open = true, onClose }: {
  images: GalleryImage[], selectedIndex?: number, updateHistory?: boolean, open?: boolean, onClose?: () => void,
}) {
  const history = useHistory();
  const { t } = useTranslation();
  const swiperRef = useRef<SwiperClass>();
  const [slideIndex, setSlideIndex] = useState(selectedIndex);
  const selected = images[slideIndex];

  const handleDownloadClick = useCallback((e) => {
    if (e.preventDefault) e.preventDefault();
    downloadImage(getImage(selected.image));
  }, [selected]);

  useUpdateEffect(() => {
    if (selectedIndex === slideIndex) return;
    setSlideIndex(selectedIndex);
  }, [selectedIndex]);
  useUpdateEffect(() => {
    if (!updateHistory) return;
    const selected = images[slideIndex];
    history.replace(galleryDetailedRoute(selected.id), selected);
  }, [slideIndex]);
  return (
    <Dialog className={css.GalleryDialog} open={open} onClose={onClose}>
      {selected && (<div className={css.downloadWrap}>
        <ContainButton
          className={css.downloadBtn} startIcon={<DownloadIcon />} color="inherit"
          // href={getImage(selected.image)} target="_blank" referrerPolicy="no-referrer" rel="noreferrer"
          onClick={handleDownloadClick}
        >
          <Typography>{t('common.download')}</Typography>
        </ContainButton>
      </div>)}
      <GallerySwiper
        forwardedRef={swiperRef}
        className={css.DialogContainer}
        showActions
        images={images}
        slideIndex={slideIndex}
        onIndexChange={setSlideIndex}
      />
    </Dialog>
  );
}

export function GallerySwiper(
  {
    className, forwardedRef, images, slideIndex: index, onIndexChange, showActions = false,
  }: ClassName & {
    images: GalleryImage[], slideIndex: number, onIndexChange?: (index: number) => void, showActions?: boolean
    forwardedRef?: MutableRefObject<SwiperClass | undefined>
  }) {
  const swiperRef = useRef<SwiperClass>();
  const [slideIndex, setSlideIndex] = useState(index);

  const lastIndex = Math.max(0, images.length - 1);

  useUpdateEffect(() => {
    if (index === slideIndex) return;
    setSlideIndex(index);
    swiperRef.current?.slideTo(index, 0);
  }, [index]);

  useEffect(() => {
    if (forwardedRef) forwardedRef.current = swiperRef.current;
    return () => {
      if (forwardedRef) forwardedRef.current = undefined;
    };
  }, [forwardedRef]);
  useEffect(() => {
    onIndexChange?.(slideIndex);
  }, [slideIndex, onIndexChange]);
  return (
    <div className={classnames(css.GallerySwiper, className)}>
      <Swiper
        reInit={false}
        forwardedRef={swiperRef}
        className={classnames(css.Swiper, 'Swiper', { [css.withActions]: showActions })}
        centeredSlides
        slidesPerView="auto"
        watchSlidesProgress
        lazy={{ loadPrevNext: true }}
        initialSlide={slideIndex}
        effect="coverflow"
        coverflowEffect={coverflowEffect}
        // touchStartPreventDefault={false}
        onTransitionEnd={({ activeIndex }) => {
          if (activeIndex === slideIndex) return;
          setSlideIndex(activeIndex);
        }}
      >
        {images.map((i, ind) => (
          <SwiperSlide
            key={i.id} className={css.SwiperSlide}
            onClick={ind === slideIndex - 1 ? () => swiperRef.current?.slidePrev()
              : (ind === slideIndex + 1 ? () => swiperRef.current?.slideNext() : undefined)}
          >
            <LazyImage className={css.image} item={i} />
          </SwiperSlide>
        ))}
        {showActions && (<div className={css.actions}>
          <LeftArrowIconButton disabled={slideIndex === 0} onClick={() => swiperRef.current?.slidePrev()} />
          <RightArrowIconButton disabled={slideIndex === lastIndex}
                                onClick={() => swiperRef.current?.slideNext()} />
        </div>)}
      </Swiper>
    </div>
  );
}

function LazyImage({ className, item }: ClassName & { item: GalleryImage }) {
  return (
    <>
      <img
        className={classnames('swiper-lazy', className)}
        // style={{ display: 'none' }}
        // onLoad={(e) => {
        //   // @ts-ignore
        //   e.target.style.display = 'initial';
        // }}
        data-src={getGalleryImage(item)} data-alt={`$изображение-${item.id}`} alt=""
      />
      <div className="swiper-lazy-preloader" />
    </>
  );
}
